import React, {PureComponent} from 'react';
import {Empty, Spin} from 'antd';
import {withTranslation, WithTranslation} from 'react-i18next'
import {Bar, defaults, Line} from "react-chartjs-2";
import {TimesheetSummary} from '../stores/timesheets-store';
import {EmployeeReport, EmployeesReport} from '../stores/employee-report-store';
import autobind from 'autobind-decorator';

defaults.global.defaultFontFamily = "'Avenir','Helvetica Neue','Arial','Helvetica',sans-serif"
defaults.global.defaultFontSize = 14

export interface TimesheetTaskEntryItem {
    day: number,
    planned: number,
    hours: number
}

export interface TimesheetTaskItem {
    projectTaskId: string,
    title: string,
    taskTitle: string,
    projectTitle: string,
    entries: TimesheetTaskEntryItem[],
}

export interface TimesheetItem extends TimesheetSummary {
    tasks: TimesheetTaskItem[]
}

interface EmployeesReportChartProps extends WithTranslation {
    data: EmployeesReport,
    projectId: string,
    colors: string[],
    renderToggle: boolean,
    showBar?: boolean,
    showPie?: boolean,
    time: boolean,
    cost: boolean,
    cir: boolean,
}

interface EmployeesReportChartState {
    renderToggle: boolean,
    isBusy: boolean,
}



type LineReport = Pick<EmployeeReport, 'employeeName' | 'totalWorkedHours' | 'estimatedHourlyCost' | 'totalWorkedHoursByMonth'>

class SchemeReportChart extends PureComponent<EmployeesReportChartProps, EmployeesReportChartState> {

    constructor(props: EmployeesReportChartProps) {
        super(props);

        this.state = {
            renderToggle: this.props.renderToggle,
            isBusy: false,
        }
    }
    private parseTime(h: any, format?: any) {
        if (format == null) {
            var hours = parseFloat(h).toFixed(2)
            return hours
        }
    }

    @autobind
    private buildLineData(projectTaskIdToName: Map<string, string>, reports: EmployeeReport[]): LineReport[] {
        const self = this;
        var tasksIds: string[] = []
        var projectNames: string[] = []
        projectTaskIdToName.forEach((name: any, id: any) => {
            tasksIds.push(id)
            projectNames.push(name)
        })
        var newReports: LineReport[] = [];

        for (let report of reports) {
            const r: LineReport = {
                employeeName: report.employeeName,
                estimatedHourlyCost: report.estimatedHourlyCost,
                totalWorkedHours: 0,
                totalWorkedHoursByMonth: new Array<number>(12).fill(0),
            }
            var addReport = false
            report.workedHoursPerTask.forEach((h, taskId) => {
                if ((self.props.cir && self.props.data.projectIdToCir.get(self.props.data.tasksToProjects.get(taskId)!) && self.props.data.taskIdToCir.get(taskId)) || (!self.props.cir && this.props.data.projectIdToCii.get(self.props.data.tasksToProjects.get(taskId)!) && self.props.data.taskIdToCii.get(taskId))) {
                    const workedHoursPerTaskByMonth = report.workedHoursPerTaskByMonth.get(taskId)!;
                    for (var i = 0; i < 12; i++) r.totalWorkedHoursByMonth[i] += workedHoursPerTaskByMonth[i];
                    r.totalWorkedHours += report.workedHoursPerTask.get(taskId)!
                    addReport = true
                }
            })
            if (addReport) {
                newReports.push(r)
            }
        }
        return newReports
    }

    render() {
        var self = this
        const { t } = this.props;
        const showBar = this.props.showBar
        const showPie = this.props.showPie
        const colors = this.props.colors

        //build labels legend
        var projectTaskIds: string[] = []
        var projectTaskNames: string[] = []
        Array.from(this.props.data.projectTaskIdToName).forEach((item) => {
            var projectId = this.props.data.tasksToProjects.get(item[0])
            if ((this.props.cir && this.props.data.projectIdToCir.get(projectId!) && this.props.data.taskIdToCir.get(item[0])) || (!this.props.cir && this.props.data.projectIdToCii.get(projectId!) && this.props.data.taskIdToCii.get(item[0]))) {
                projectTaskIds.push(item[0])
                projectTaskNames.push(this.props.data.projectIdToName.get(projectId!) + '-' + item[1])
            }
        })

        var lineData = this.buildLineData(this.props.data.projectTaskIdToName, this.props.data.reports)

        const maxTotalHours = lineData.reduce((max, obj) => {
            return obj.totalWorkedHours > max ? obj.totalWorkedHours : max
        }, -Infinity);

        let dataC = {
            labels: projectTaskNames,
            datasets: this.props.data.reports.map((report, index) => {
                return {
                    label: report.employeeName,
                    data: projectTaskIds.map(id => (this.parseTime((report.workedHoursPerTask.get(id) ?? 0) * report.estimatedHourlyCost))),
                    fill: true,
                    backgroundColor: colors[index],
                    hoverBackgroundColor: colors[index],
                    borderColor: 'rgba(200, 200, 200, 0.75)',
                    hoverBorderColor: 'rgba(200, 200, 200, 1)'
                }
            })
        }

        let dataT = {
            labels: projectTaskNames,
            datasets: this.props.data.reports.map((report, index) => {
                return {
                    label: report.employeeName,
                    data: projectTaskIds.map(id => (this.parseTime(report.workedHoursPerTask.get(id))) ?? 0),
                    fill: true,
                    backgroundColor: colors[index],
                    hoverBackgroundColor: colors[index],
                    borderColor: 'rgba(200, 200, 200, 0.75)',
                    hoverBorderColor: 'rgba(200, 200, 200, 1)'
                }
            })
        }

        let optionsT = {
            scales: {
                yAxes: [{
                    ticks: {
                        beginAtZero: true
                    }
                }],
                xAxes: [{
                    barPercentage: 0.4
                }]
            },
            responsive: true,
            maintainAspectRatio: false,
            title: {
                display: true,
                text: t("Worked hours per task")
            }
        }
        let optionsC = {
            scales: {
                yAxes: [{
                    ticks: {
                        beginAtZero: true
                    }
                }],
                xAxes: [{
                    barPercentage: 0.4
                }]
            },
            responsive: true,
            maintainAspectRatio: false,
            title: {
                display: true,
                text: t("Estimated cost per task")
            }
        }

        const monthNames = [t("January"), t("February"), t("March"), t("April"), t("May"), t("June"),
        t("July"), t("August"), t("September"), t("October"), t("November"), t("December")];

        const lineDataTime = {
            datasets: lineData.map((r, index) => ({
                data: r.totalWorkedHoursByMonth,
                borderColor: colors[index],
                label: r.employeeName,
                fill: false,
                tension: 0
            })),
            labels: monthNames
        }

        const lineDataTImeCost = {
            datasets: lineData.map((r, index) => ({
                data: r.totalWorkedHoursByMonth.map(h => h * r.estimatedHourlyCost),
                borderColor: colors[index],
                label: r.employeeName,
                fill: false,
                tension: 0
            })),
            labels: monthNames
        }
        const lineOptionsTime = {
            responsive: true,
            maintainAspectRatio: false,
            title: {
                display: true,
                text: t("Total worked hours by month")
            }
        };
        let lineOptionsTimeCost = {
            responsive: true,
            maintainAspectRatio: false,
            title: {
                display: true,
                text: t("Total estimated cost by month")
            }
        }

        if (lineData.length == 0) { return null }
        const heightValue = maxTotalHours > 200 || this.props.data.reports.length > 10 ? '140vh' : '70vh';

        const containerStyle = {
            padding: 10,
            width: "100%",
            height: heightValue,
            marginBottom: '30px'
        };

        const chartStyle = {
            width: "50%",
            height: heightValue
        };

        return <Spin size='large' spinning={this.state.isBusy} style={{ width: "100%" }}>
            {this.props.time &&
                <div className="flex-container" style={containerStyle}>
                    {showBar &&
                        < div className="flex" style={chartStyle}>
                            <Bar
                                data={dataT}
                                options={optionsT}
                                redraw={true}
                            />
                        </div>}
                    {showPie &&
                        < div className="flex" style={chartStyle}>
                            <Line data={lineDataTime} options={lineOptionsTime} />
                        </div>}
                </div>
            }
            {this.props.cost &&
                <div className="flex-container" style={containerStyle}>
                    {showBar &&
                        < div className="flex" style={chartStyle}>
                            <Bar
                                data={dataC}
                                options={optionsC}
                                redraw={true}
                            />
                        </div>}
                    {showPie &&
                        < div className="flex" style={chartStyle}>
                            <Line data={lineDataTImeCost} options={lineOptionsTimeCost} />
                        </div>}
                </div>
            }
            {!this.props.time && !this.props.cost && <Empty />}
        </Spin>
    }
}
export default withTranslation()(SchemeReportChart);
